// src/pages/PricingPage.js
import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaCheck, FaTimes, FaBolt, FaStar, FaCrown, FaAndroid,
  FaChartLine, FaBell, FaShieldAlt, FaUserFriends,
} from "react-icons/fa";

/* ── Animations ─────────────────────────────────────────────────────────────── */
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-6px); }
`;
const pulseBadge = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(247,148,29,0.4); }
  50%       { box-shadow: 0 0 0 8px rgba(247,148,29,0); }
`;

/* ── Page wrapper ───────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: linear-gradient(160deg, #f0f9ff 0%, #ffffff 50%, #fef6e4 100%);
  color: var(--text-color);
  padding: 0 0 6rem;
`;

/* ── Hero ───────────────────────────────────────────────────────────────────── */
const Hero = styled.section`
  padding: 4rem 8% 3rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -120px; left: 50%;
    transform: translateX(-50%);
    width: 700px; height: 700px;
    background: radial-gradient(circle, rgba(41,171,226,0.08) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const HeroEyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(41,171,226,0.1);
  border: 1px solid rgba(41,171,226,0.25);
  color: var(--primary-dark);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 0.35rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.2rem;
`;

const HeroH1 = styled(motion.h1)`
  font-size: clamp(2.4rem, 5vw, 3.8rem);
  line-height: 1.1;
  margin: 0 0 1.1rem;
  background: linear-gradient(135deg, var(--navy-color) 0%, var(--primary-dark) 60%, var(--accent-color) 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 4s linear infinite;
`;

const HeroSub = styled(motion.p)`
  font-size: 1.15rem;
  color: var(--text-secondary-color);
  max-width: 620px;
  margin: 0 auto 2rem;
  line-height: 1.75;
`;

const AndroidBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #1a1a2e;
  color: #ffffff;
  text-decoration: none;
  padding: 0.55rem 1.2rem;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.25);
  }

  svg { color: #3ddc84; font-size: 1.1rem; }
`;

const TrustRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.8rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-secondary-color);

  svg { color: var(--accent-color); }
`;


/* ── Pricing cards ──────────────────────────────────────────────────────────── */
const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  padding: 0 8%;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const PlanCard = styled(motion.div)`
  background: ${({ $popular }) => $popular
    ? 'linear-gradient(160deg, var(--navy-color) 0%, var(--primary-dark) 100%)'
    : '#ffffff'};
  border: ${({ $popular }) => $popular ? 'none' : '1px solid var(--border-color)'};
  border-radius: 22px;
  padding: 2rem 1.8rem 1.8rem;
  position: relative;
  box-shadow: ${({ $popular }) => $popular
    ? '0 24px 60px rgba(26,82,118,0.3)'
    : '0 4px 20px rgba(0,0,0,0.05)'};
  ${({ $popular }) => $popular && 'transform: scale(1.04);'}

  @media (max-width: 860px) {
    transform: none !important;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--accent-color);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.28rem 1rem;
  border-radius: 20px;
  white-space: nowrap;
  animation: ${pulseBadge} 2.4s ease-in-out infinite;
`;

const PlanIcon = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  margin-bottom: 1rem;
  background: ${({ $popular }) => $popular ? 'rgba(255,255,255,0.12)' : 'rgba(41,171,226,0.1)'};
  color: ${({ $popular, $accent }) => $popular ? '#ffffff' : $accent ? 'var(--accent-color)' : 'var(--primary-color)'};
  border: 1px solid ${({ $popular }) => $popular ? 'rgba(255,255,255,0.2)' : 'var(--border-color)'};
`;

const PlanName = styled.h2`
  font-size: 1.1rem;
  font-weight: 900;
  margin: 0 0 0.25rem;
  color: ${({ $popular }) => $popular ? '#ffffff' : 'var(--text-color)'};
  letter-spacing: 0.02em;
`;

const PlanTagline = styled.p`
  font-size: 0.8rem;
  color: ${({ $popular }) => $popular ? 'rgba(255,255,255,0.65)' : 'var(--text-body-soft)'};
  margin: 0 0 1.2rem;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.3rem;
  margin-bottom: 0.3rem;
`;

const PriceAmount = styled.span`
  font-size: 2.6rem;
  font-weight: 900;
  line-height: 1;
  color: ${({ $popular }) => $popular ? '#ffffff' : 'var(--text-color)'};
`;

const PricePer = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ $popular }) => $popular ? 'rgba(255,255,255,0.6)' : 'var(--text-body-soft)'};
  margin-bottom: 0.35rem;
`;

const PriceAnnual = styled.p`
  font-size: 0.75rem;
  color: ${({ $popular }) => $popular ? 'rgba(255,255,255,0.55)' : 'var(--text-body-soft)'};
  margin: 0 0 1.5rem;
  min-height: 1.1rem;
`;

const PlanCTA = styled.a`
  display: block;
  text-align: center;
  padding: 0.82rem 1rem;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  margin-bottom: 1.5rem;
  background: ${({ $popular }) => $popular
    ? 'var(--accent-color)'
    : 'var(--primary-color)'};
  color: #ffffff;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px ${({ $popular }) => $popular
      ? 'rgba(247,148,29,0.4)'
      : 'rgba(41,171,226,0.35)'};
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ $popular }) => $popular ? 'rgba(255,255,255,0.12)' : 'var(--border-color)'};
  margin: 0 0 1.2rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.62rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-size: 0.82rem;
  color: ${({ $popular }) => $popular ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary-color)'};
  line-height: 1.4;
`;

const CheckIcon = styled(FaCheck)`
  color: ${({ $popular }) => $popular ? '#6ee7b7' : '#059669'};
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 0.72rem;
`;

const CrossIcon = styled(FaTimes)`
  color: var(--text-body-soft);
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 0.72rem;
  opacity: 0.5;
`;

/* ── What's included section ────────────────────────────────────────────────── */
const Section = styled.section`
  padding: 4rem 8% 1rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 2.8rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  margin: 0 0 0.8rem;
`;

const SectionDesc = styled.p`
  color: var(--text-secondary-color);
  max-width: 620px;
  margin: 0 auto;
  line-height: 1.75;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.4rem;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
  @media (max-width: 1000px) and (min-width: 720px) { grid-template-columns: repeat(2, 1fr); }
`;

const FeatCard = styled(motion.div)`
  background: #ffffff;
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1.5rem;
  border-top: 3px solid ${({ $color }) => $color || 'var(--primary-color)'};
`;

const FeatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ $color }) => $color ? `${$color}15` : 'rgba(41,171,226,0.1)'};
  border: 1px solid ${({ $color }) => $color ? `${$color}30` : 'rgba(41,171,226,0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: ${({ $color }) => $color || 'var(--primary-color)'};
  margin-bottom: 0.9rem;
`;

const FeatTitle = styled.h3`
  font-size: 1rem;
  margin: 0 0 0.45rem;
`;

const FeatText = styled.p`
  font-size: 0.85rem;
  color: var(--text-secondary-color);
  margin: 0;
  line-height: 1.65;
`;

/* ── Comparison table ───────────────────────────────────────────────────────── */
const TableWrap = styled.div`
  padding: 2rem 8% 3rem;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
`;

const Thead = styled.thead`
  background: var(--bg-secondary);
`;

const Th = styled.th`
  padding: 0.9rem 1rem;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  font-weight: 800;
  color: var(--text-color);
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
  &:first-child { border-radius: 12px 0 0 0; }
  &:last-child  { border-radius: 0 12px 0 0; }
`;

const ThPro = styled(Th)`
  background: linear-gradient(135deg, var(--navy-color), var(--primary-dark));
  color: #ffffff;
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--border-color);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--bg-secondary); }
`;

const Td = styled.td`
  padding: 0.8rem 1rem;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  color: var(--text-secondary-color);
`;

const TdFeature = styled(Td)`
  font-weight: 600;
  color: var(--text-color);
`;

/* ── CTA strip ──────────────────────────────────────────────────────────────── */
const CTASection = styled.section`
  padding: 2rem 8% 1rem;
`;

const CTABlock = styled.div`
  max-width: 860px;
  margin: 0 auto;
  background: linear-gradient(135deg, var(--navy-color) 0%, var(--primary-dark) 100%);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 240px; height: 240px;
    background: rgba(255,255,255,0.04);
    border-radius: 50%;
  }
`;

const CTATitle = styled.h2`
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  color: #ffffff;
  margin: 0 0 0.8rem;
`;

const CTAText = styled.p`
  color: rgba(255,255,255,0.7);
  max-width: 560px;
  margin: 0 auto 1.8rem;
  line-height: 1.75;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const CTAPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--accent-color);
  color: #ffffff;
  font-weight: 800;
  font-size: 0.95rem;
  padding: 0.9rem 1.8rem;
  border-radius: 14px;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  animation: ${float} 3s ease-in-out infinite;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 36px rgba(247,148,29,0.45);
  }
`;

const CTASecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255,255,255,0.1);
  color: #ffffff;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 0.9rem 1.6rem;
  border-radius: 14px;
  text-decoration: none;
  border: 1px solid rgba(255,255,255,0.2);
  transition: background 0.2s;

  &:hover { background: rgba(255,255,255,0.18); }
`;

/* ── FAQ ────────────────────────────────────────────────────────────────────── */
const FAQSection = styled.section`
  padding: 4rem 8% 2rem;
  max-width: 900px;
  margin: 0 auto;
`;

const FAQTitle = styled.h2`
  text-align: center;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  margin: 0 0 2.2rem;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid var(--border-color);
  padding: 1.2rem 0;
  &:last-child { border-bottom: none; }
`;

const Q = styled.h3`
  font-size: 1.05rem;
  margin: 0 0 0.55rem;
`;

const A = styled.p`
  margin: 0;
  color: var(--text-secondary-color);
  line-height: 1.75;
  font-size: 0.95rem;
`;

/* ── Data ───────────────────────────────────────────────────────────────────── */

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    icon: <FaBolt />,
    tagline: 'Start exploring AI signals',
    monthlyPrice: '0',
    note: 'Always free — no card needed',
    cta: 'Download Free on Android',
    ctaHref: 'https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie',
    popular: false,
    accent: false,
    features: [
      { text: 'AI crypto signals (limited)', included: true },
      { text: 'Live crypto news feed', included: true },
      { text: 'Basic price alerts', included: true },
      { text: 'BTC & ETH market overview', included: true },
      { text: 'Derivatives pulse data', included: false },
      { text: 'Fibonacci retracement levels', included: false },
      { text: 'Long/short ratio & open interest', included: false },
      { text: 'Unlimited push notifications', included: false },
      { text: 'AI stock & forex signals', included: false },
      { text: 'Priority signal delivery', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Premium',
    icon: <FaCrown />,
    tagline: 'Full AI signal access for active traders',
    monthlyPrice: '9.99',
    note: '7-day free trial — cancel anytime',
    cta: 'Start Premium — Free 7-Day Trial',
    ctaHref: 'https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie',
    popular: true,
    accent: false,
    features: [
      { text: 'Unlimited AI crypto signals', included: true },
      { text: 'Live crypto news feed', included: true },
      { text: 'Unlimited price alerts', included: true },
      { text: 'Full multi-coin market coverage', included: true },
      { text: 'Live derivatives pulse (30s refresh)', included: true },
      { text: 'Fibonacci retracement levels (live)', included: true },
      { text: 'Long/short ratio & open interest', included: true },
      { text: 'Unlimited push notifications', included: true },
      { text: 'AI stock & forex signals', included: true },
      { text: 'Priority signal delivery', included: true },
    ],
  },
];

const APP_FEATURES = [
  {
    icon: <FaBolt />,
    color: '#F7941D',
    title: 'Real-Time AI Signals',
    text: 'AI-generated crypto trade signals delivered to your Android device the moment conditions align — no manual scanning required.',
  },
  {
    icon: <FaChartLine />,
    color: '#29ABE2',
    title: 'Live Derivatives Intelligence',
    text: 'Funding rates, long/short ratios, open interest, and Fibonacci retracement levels — all updating every 30 seconds.',
  },
  {
    icon: <FaBell />,
    color: '#6366f1',
    title: 'Smart Push Notifications',
    text: 'Get alerted the moment a high-quality signal appears. Notifications include signal type, direction, and key price levels.',
  },
  {
    icon: <FaShieldAlt />,
    color: '#059669',
    title: 'Risk Management Tools',
    text: 'Built-in position sizing guidance and drawdown awareness so you always trade with a defined risk per trade.',
  },
  {
    icon: <FaAndroid />,
    color: '#3ddc84',
    title: 'Android-First Design',
    text: 'Designed specifically for Android with a clean, fast interface that puts the most important data front and centre.',
  },
  {
    icon: <FaUserFriends />,
    color: '#f43f5e',
    title: 'Multi-Asset Coverage',
    text: 'Signals across crypto, stocks, and forex in one app. Follow BTC, ETH, SOL, top altcoins, and major equity movers.',
  },
];

const TABLE_FEATURES = [
  { label: 'AI crypto signals', free: 'Limited', pro: 'Unlimited' },
  { label: 'Price alerts', free: 'Basic', pro: 'Unlimited' },
  { label: 'Push notifications', free: 'Limited', pro: '✓' },
  { label: 'Live crypto news feed', free: '✓', pro: '✓' },
  { label: 'Derivatives pulse (funding, L/S, OI)', free: '—', pro: '✓' },
  { label: 'Fibonacci retracement levels (live)', free: '—', pro: '✓' },
  { label: 'Long/short ratio & open interest', free: '—', pro: '✓' },
  { label: 'AI stock & forex signals', free: '—', pro: '✓' },
  { label: 'Priority signal delivery', free: '—', pro: '✓' },
  { label: 'Full multi-coin market coverage', free: '—', pro: '✓' },
  { label: 'Free trial', free: '—', pro: '7 days' },
];

/* ── Component ──────────────────────────────────────────────────────────────── */
const PricingPage = () => {

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Trend Genie — AI Crypto Signals",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Android",
    url: "https://www.trendgenie.io/pricing",
    description:
      "Trend Genie is an AI-powered crypto trading signals app for Android. Get real-time buy/sell signals, live derivatives data, Fibonacci retracement levels, and smart push notifications.",
    offers: [
      {
        "@type": "Offer",
        name: "Free Plan",
        price: "0",
        priceCurrency: "USD",
        description: "Limited AI crypto signals, basic price alerts, and live news feed. No credit card required.",
      },
      {
        "@type": "Offer",
        name: "Premium Plan",
        price: "9.99",
        priceCurrency: "USD",
        description: "Unlimited AI crypto, stock and forex signals, live derivatives data, Fibonacci retracement levels, unlimited alerts, and priority signal delivery.",
      },
    ],
    publisher: {
      "@type": "Organization",
      name: "Trend Genie",
      url: "https://www.trendgenie.io",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Is Trend Genie free on Android?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Trend Genie has a free plan available on Android with 5 AI crypto signals per day, basic price alerts, and a live news feed. No credit card is required to get started.",
        },
      },
      {
        "@type": "Question",
        name: "What is included in the Pro plan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The Pro plan includes unlimited AI crypto signals, live derivatives data (funding rates, long/short ratios, open interest), real-time Fibonacci retracement levels for BTC, ETH, SOL and BNB, unlimited push notifications, and unlimited price alerts.",
        },
      },
      {
        "@type": "Question",
        name: "Can I cancel my Trend Genie subscription at any time?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Subscriptions are managed through Google Play and can be cancelled at any time. Your plan remains active until the end of the current billing period.",
        },
      },
      {
        "@type": "Question",
        name: "Is there a free trial for Pro or Elite?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Both the Pro and Elite plans include a 7-day free trial. You will not be charged until the trial period ends.",
        },
      },
      {
        "@type": "Question",
        name: "What is included in the Premium plan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Premium includes unlimited AI crypto, stock, and forex signals, live derivatives data (funding rates, long/short ratios, open interest), real-time Fibonacci retracement levels, unlimited push notifications, unlimited price alerts, and priority signal delivery.",
        },
      },
    ],
  };

  return (
    <Page>
      <Helmet>
        <title>Trend Genie Pricing — AI Crypto Signals App For Android | Free, Pro & Elite</title>
        <meta
          name="description"
          content="Get started free or unlock full AI crypto trading signals on Android with Trend Genie Pro or Elite. Compare plans, features, and pricing. 7-day free trial on paid plans."
        />
        <meta
          name="keywords"
          content="trend genie pricing, crypto signals app pricing, AI crypto signals android app, best crypto trading app android price, crypto signals subscription android"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.trendgenie.io/pricing" />
        <script type="application/ld+json">{JSON.stringify(pricingSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <Hero>
        <HeroEyebrow><FaAndroid /> Available on Google Play</HeroEyebrow>
        <HeroH1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Simple Pricing.<br />Serious Trading Edge.
        </HeroH1>
        <HeroSub
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
        >
          Start free and upgrade when you're ready. Every plan gives you access to
          Trend Genie's AI signal engine on Android — no hidden fees, no locked charts.
        </HeroSub>
        <AndroidBadge
          href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaAndroid /> Download on Google Play — Free
        </AndroidBadge>
        <TrustRow>
          <TrustItem><FaStar /> 4.8 rating on Play Store</TrustItem>
          <TrustItem><FaShieldAlt /> No credit card for free plan</TrustItem>
          <TrustItem><FaCheck /> 7-day trial on paid plans</TrustItem>
        </TrustRow>
      </Hero>

      {/* ── PRICING CARDS ── */}
      <PlansGrid>
        {PLANS.map((plan, i) => (
          <PlanCard
            key={plan.id}
            $popular={plan.popular}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
          >
            {plan.popular && <PopularBadge>⚡ Most Popular</PopularBadge>}

            <PlanIcon $popular={plan.popular} $accent={plan.accent}>
              {plan.icon}
            </PlanIcon>
            <PlanName $popular={plan.popular}>{plan.name}</PlanName>
            <PlanTagline $popular={plan.popular}>{plan.tagline}</PlanTagline>

            <PriceRow>
              <PriceAmount $popular={plan.popular}>
                ${plan.monthlyPrice}
              </PriceAmount>
              {plan.monthlyPrice !== '0' && (
                <PricePer $popular={plan.popular}>/mo</PricePer>
              )}
            </PriceRow>
            <PriceAnnual $popular={plan.popular}>{plan.note}</PriceAnnual>

            <PlanCTA
              href={plan.ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              $popular={plan.popular}
            >
              {plan.cta}
            </PlanCTA>

            <Divider $popular={plan.popular} />

            <FeatureList>
              {plan.features.map((f, fi) => (
                <FeatureItem key={fi} $popular={plan.popular}>
                  {f.included
                    ? <CheckIcon $popular={plan.popular} />
                    : <CrossIcon />}
                  {f.text}
                </FeatureItem>
              ))}
            </FeatureList>
          </PlanCard>
        ))}
      </PlansGrid>

      {/* ── APP FEATURES ── */}
      <Section style={{ paddingTop: '5rem' }}>
        <SectionHeader>
          <SectionTitle>Everything You Need To Trade Smarter On Android</SectionTitle>
          <SectionDesc>
            Trend Genie is purpose-built for active crypto traders on Android. Every feature
            is designed to save time, reduce noise, and improve decision quality.
          </SectionDesc>
        </SectionHeader>
        <FeaturesGrid>
          {APP_FEATURES.map((f, i) => (
            <FeatCard
              key={i}
              $color={f.color}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <FeatIcon $color={f.color}>{f.icon}</FeatIcon>
              <FeatTitle>{f.title}</FeatTitle>
              <FeatText>{f.text}</FeatText>
            </FeatCard>
          ))}
        </FeaturesGrid>
      </Section>

      {/* ── COMPARISON TABLE ── */}
      <TableWrap>
        <SectionHeader>
          <SectionTitle>Full Feature Comparison</SectionTitle>
          <SectionDesc>See exactly what's included in each plan before you decide.</SectionDesc>
        </SectionHeader>
        <Table>
          <Thead>
            <tr>
              <Th>Feature</Th>
              <Th $center>Free</Th>
              <ThPro $center>Premium 👑</ThPro>
            </tr>
          </Thead>
          <tbody>
            {TABLE_FEATURES.map((row, i) => (
              <Tr key={i}>
                <TdFeature>{row.label}</TdFeature>
                <Td $center>{row.free}</Td>
                <Td $center style={{ fontWeight: row.pro === '✓' ? 700 : 400, color: row.pro === '✓' ? '#059669' : undefined }}>{row.pro}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {/* ── CTA ── */}
      <CTASection>
        <CTABlock>
          <CTATitle>Start Trading Smarter Today</CTATitle>
          <CTAText>
            Download Trend Genie free on Android and experience AI-powered crypto signals,
            live derivatives data, and real-time Fibonacci levels — all in one app.
          </CTAText>
          <CTAButtons>
            <CTAPrimary
              href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaAndroid /> Download Free on Android
            </CTAPrimary>
            <CTASecondary to="/insights">
              Read Trading Guides →
            </CTASecondary>
          </CTAButtons>
        </CTABlock>
      </CTASection>

      {/* ── FAQ ── */}
      <FAQSection>
        <FAQTitle>Pricing FAQ</FAQTitle>

        <FAQItem>
          <Q>Is Trend Genie free on Android?</Q>
          <A>
            Yes. Trend Genie has a free plan on Android with 5 AI crypto signals per day,
            basic price alerts, and a live news feed. No credit card is required to get started.
          </A>
        </FAQItem>

        <FAQItem>
          <Q>What's included in the Pro plan?</Q>
          <A>
            Pro unlocks unlimited AI crypto signals, live derivatives data (funding rates,
            long/short ratios, open interest), real-time Fibonacci retracement levels for BTC,
            ETH, SOL, and BNB, unlimited push notifications, and unlimited price alerts.
          </A>
        </FAQItem>

        <FAQItem>
          <Q>Is there a free trial for Pro or Elite?</Q>
          <A>
            Yes. Both Pro and Elite include a 7-day free trial. You will not be charged until
            the trial ends. Cancel anytime through Google Play before the trial period to avoid
            any charge.
          </A>
        </FAQItem>

        <FAQItem>
          <Q>What's included in Premium?</Q>
          <A>
            Premium unlocks unlimited AI crypto, stock, and forex signals, live derivatives
            intelligence (funding rates, long/short ratios, open interest), real-time Fibonacci
            retracement levels for BTC, ETH, SOL, and BNB, unlimited push notifications,
            unlimited price alerts, and priority signal delivery.
          </A>
        </FAQItem>

        <FAQItem>
          <Q>Can I cancel my subscription at any time?</Q>
          <A>
            Yes. Subscriptions are billed and managed through Google Play. You can cancel at any
            time and your plan stays active until the end of the current billing cycle.
          </A>
        </FAQItem>

        <FAQItem>
          <Q>How much does Premium cost?</Q>
          <A>
            Premium costs $9.99/month, billed monthly through Google Play. A 7-day free trial
            is included — you will not be charged until the trial ends.
          </A>
        </FAQItem>

        <FAQItem>
          <Q>Is Trend Genie available on iOS?</Q>
          <A>
            Trend Genie is currently available on Android via Google Play. iOS availability
            will be announced through the{" "}
            <Link to="/insights">Genie Insights</Link> section when it launches.
          </A>
        </FAQItem>
      </FAQSection>
    </Page>
  );
};

export default PricingPage;
